import { useState } from 'react';

import { RadioGroup, RadioGroupItem } from '@nesso/ui/primitives/radio-group';
import { RadioGroupItemField } from '@nesso/ui/primitives/radio-group-item-field';

import {
    type NextSessionType,
    nextSessionSchema,
    type PlanInput,
    planSchema,
} from '@/entities/session-note';
import { slugify } from '@/shared/lib/format-utils';
import {
    formatLocalDate,
    getNextWeekSameWeekday,
    getStartOfDay,
    parseLocalDate,
} from '@/shared/lib/time-utils';
import { CheckboxListWithOther } from '@/shared/ui/checkbox-list/checkbox-list-with-other';
import { NoteTextarea } from '@/shared/ui/common/note-textarea';
import { FieldSection } from '@/shared/ui/forms/field-section';

import { NessoPlanDatePicker } from './nesso-plan-section/nesso-plan-date-picker';

type PlanContent = PlanInput;

const NEXT_SESSION_OPTIONS: { value: NextSessionType; label: string }[] =
    nextSessionSchema.options.map((variant) => ({
        value: variant.shape.nextSession.value,
        label: variant.shape.nextSession.value,
    }));

const HOMEWORK_OPTIONS = planSchema.shape.homework
    .unwrap()
    .element.options.map((label) => ({ value: label, label }));

type Props = {
    content: PlanContent;
    onChange: (content: PlanContent) => void;
};

export function NessoPlanSection({ content, onChange }: Props) {
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const today = getStartOfDay(new Date());

    const currentType = content.nextSession?.nextSession;
    const currentDate =
        content.nextSession?.nextSession === 'Specify date' ? content.nextSession.date : undefined;

    const handleTypeChange = (type: NextSessionType) => {
        if (type === 'Specify date') {
            const date =
                (content.nextSession?.nextSession === 'Specify date'
                    ? content.nextSession.date
                    : undefined) ?? formatLocalDate(getNextWeekSameWeekday());
            onChange({
                ...content,
                nextSession: { nextSession: 'Specify date', date },
            });
            setIsPickerOpen(true);
        } else {
            onChange({ ...content, nextSession: { nextSession: type } });
            setIsPickerOpen(false);
        }
    };

    const handleDateChange = (date: Date | undefined) => {
        if (!date) return;
        onChange({
            ...content,
            nextSession: { nextSession: 'Specify date', date: formatLocalDate(date) },
        });
    };

    return (
        <FieldSection title="Plan" className="gap-9">
            <FieldSection label="Next session scheduled for:" className="gap-4">
                <RadioGroup<NextSessionType | ''>
                    value={currentType ?? ''}
                    onValueChange={(value) => {
                        // '' is only the group's nothing-selected value; no item emits it.
                        if (value !== '') handleTypeChange(value);
                    }}
                >
                    {NEXT_SESSION_OPTIONS.map((option) => {
                        const id = `next-${slugify(option.value)}`;
                        if (option.value === 'Specify date') {
                            return (
                                <div key={option.value} className="flex items-center gap-3">
                                    <RadioGroupItem id={id} value={option.value} />
                                    <NessoPlanDatePicker
                                        value={
                                            currentDate ? parseLocalDate(currentDate) : undefined
                                        }
                                        onChange={handleDateChange}
                                        open={isPickerOpen}
                                        onOpenChange={setIsPickerOpen}
                                        minDate={today}
                                        disabled={currentType !== 'Specify date'}
                                    />
                                </div>
                            );
                        }
                        return (
                            <RadioGroupItemField
                                key={option.value}
                                id={id}
                                value={option.value}
                                label={option.label}
                                fieldProps={{ className: 'gap-3' }}
                            />
                        );
                    })}
                </RadioGroup>
            </FieldSection>

            <FieldSection label="Homework:">
                <CheckboxListWithOther
                    columns={1}
                    options={HOMEWORK_OPTIONS}
                    values={content.homework ?? []}
                    onValuesChange={(homework) =>
                        onChange({ ...content, homework: homework as PlanContent['homework'] })
                    }
                    otherValue={content.homeworkOther}
                    onOtherValueChange={(homeworkOther) => onChange({ ...content, homeworkOther })}
                />
            </FieldSection>

            <FieldSection label="Resources and recommendations">
                <NoteTextarea
                    value={content.resourcesAndRecommendations}
                    onChange={(resourcesAndRecommendations) =>
                        onChange({ ...content, resourcesAndRecommendations })
                    }
                    rows={2}
                />
            </FieldSection>

            <FieldSection label="Follow up on...">
                <NoteTextarea
                    value={content.followUpOn}
                    onChange={(followUpOn) => onChange({ ...content, followUpOn })}
                    rows={2}
                />
            </FieldSection>
        </FieldSection>
    );
}
